import { useRef, useCallback, useEffect } from "react";

/**
 * 处理移动端键盘弹起/收起产生的布局偏移。
 * - 监听 visualViewport resize 实时更新 --imc-kb-height CSS 变量
 * - iOS WKWebView 键盘顶页面修复（translateY 补偿）
 *
 * @returns {{ chatRootRef, showKeyboard, hideKeyboard }}
 */
export default function useImMobileKeyboard({ active, listRef, scrollToBottom, isRestoringScrollRef }) {
  const chatRootRef  = useRef(null);
  const kbTimerRef   = useRef(null);

  // ── visualViewport 监听 ──────────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;
    const vv = window.visualViewport;
    if (!vv) return;
    let kbFallTimer = null;
    let kbWasUp     = false;
    const onVVResize = () => {
      const kb   = Math.max(0, window.innerHeight - vv.height);
      const root = chatRootRef.current;
      const el   = listRef.current;
      if (kb > 50) {
        clearTimeout(kbFallTimer);
        root?.classList.remove('kb-falling');
        document.documentElement.style.setProperty('--imc-kb-height', kb + 'px');
        if (el && !isRestoringScrollRef.current) el.scrollTop = el.scrollHeight;
        kbWasUp = true;
      } else if (kbWasUp) {
        kbWasUp = false;
        root?.classList.add('kb-falling');
        document.documentElement.style.setProperty('--imc-kb-height', '0px');
        clearTimeout(kbFallTimer);
        kbFallTimer = setTimeout(() => {
          root?.classList.remove('kb-falling');
          scrollToBottom(true);
        }, 300);
      }
    };
    vv.addEventListener('resize', onVVResize);
    return () => { vv.removeEventListener('resize', onVVResize); clearTimeout(kbFallTimer); };
  }, [active, listRef, scrollToBottom, isRestoringScrollRef]);

  // ── iOS WKWebView：键盘顶页面偏移修复 ───────────────────────────────────
  useEffect(() => {
    if (!active) {
      clearTimeout(kbTimerRef.current);
      document.documentElement.style.removeProperty('--imc-kb-height');
      return;
    }
    const ANIM_MS = 180;
    let rafId       = null;
    let cleanTimer  = null;
    let touchActive = false;
    const onTouchStart  = () => { touchActive = true; };
    const onTouchEnd    = () => { touchActive = false; };
    const restoreScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      if (y <= 0) return;
      if (touchActive) {
        document.documentElement.scrollTo({ top: 0, behavior: 'instant' });
        return;
      }
      const root = chatRootRef.current;
      if (!root) {
        document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      // 只关闭 transform 过渡（让位移瞬间完成），保留 bottom 过渡（键盘弹起平滑）
      root.style.transition = `transform 0s, bottom 0.25s ease`;
      root.style.transform  = `translateY(${y}px)`;
      document.documentElement.scrollTo({ top: 0, behavior: 'instant' });
      cancelAnimationFrame(rafId);
      clearTimeout(cleanTimer);
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(() => {
          root.style.transition = `transform ${ANIM_MS}ms ease-out, bottom 0.25s ease`;
          root.style.transform  = 'translateY(0)';
          cleanTimer = setTimeout(() => { root.style.transition = ''; root.style.transform = ''; }, ANIM_MS + 30);
        });
      });
    };
    window.addEventListener('scroll', restoreScroll);
    document.addEventListener('touchstart',  onTouchStart, { passive: true });
    document.addEventListener('touchend',    onTouchEnd,   { passive: true });
    document.addEventListener('touchcancel', onTouchEnd,   { passive: true });
    const vv = window.visualViewport;
    if (vv) vv.addEventListener('scroll', restoreScroll);
    return () => {
      window.removeEventListener('scroll', restoreScroll);
      document.removeEventListener('touchstart',  onTouchStart);
      document.removeEventListener('touchend',    onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
      if (vv) vv.removeEventListener('scroll', restoreScroll);
      cancelAnimationFrame(rafId);
      clearTimeout(cleanTimer);
      clearTimeout(kbTimerRef.current);
      document.documentElement.style.removeProperty('--imc-kb-height');
      const root = chatRootRef.current;
      if (root) { root.style.transition = ''; root.style.transform = ''; }
    };
  }, [active]);

  // ── 备用（不支持 visualViewport 的环境）─────────────────────────────────
  const showKeyboard = useCallback(() => {
    clearTimeout(kbTimerRef.current);
    kbTimerRef.current = setTimeout(() => {
      const vv = window.visualViewport;
      if (!vv) return;
      const h = Math.max(0, window.innerHeight - vv.height);
      if (h > 50) {
        document.documentElement.style.setProperty('--imc-kb-height', h + 'px');
        requestAnimationFrame(() => scrollToBottom());
      }
    }, 400);
  }, [scrollToBottom]);

  const hideKeyboard = useCallback(() => {
    clearTimeout(kbTimerRef.current);
    document.documentElement.style.setProperty('--imc-kb-height', '0px');
  }, []);

  return { chatRootRef, showKeyboard, hideKeyboard };
}
